import { getHtml, setHtml } from "client/Components/RichTextEditor/htmlConversion";
import { isProseMirror, RichTextEditorState, RichTextEditorStateKind } from "client/Components/RichTextEditor/types";
import { IInputValidation, InputValidationResultKind, IValueValidation } from "client/Validation/input";
import { IValueReducer } from "../reducer";
import { NewValueHandler } from "./misc";
import { RichTextEditorIntermediateStateHandlers } from "client/Components";

interface IAttachableRichTextEditorProps {
	readonly value: RichTextEditorState;
	readonly isInvalid: boolean;
	readonly validation: IInputValidation | undefined;
	readonly trackingId: string;
	onChange(newValue: RichTextEditorState, isSignificant: boolean): void;
	onBlur(newValue: RichTextEditorState): void;
}

interface IAttachableRichTextEditorPropsWithBuilder extends IAttachableRichTextEditorProps {
	withOnChange(handler: (newValue: RichTextEditorState) => void): IAttachableRichTextEditorPropsWithBuilder;
	withOnBlur(handler: (newValue: RichTextEditorState) => void): IAttachableRichTextEditorPropsWithBuilder;
}

export class AttachableRichTextEditorPropsWithBuilder implements IAttachableRichTextEditorPropsWithBuilder {
	private readonly _extraOnChangeHandlers: NewValueHandler<RichTextEditorState>[];
	private readonly _extraOnBlurHandlers: NewValueHandler<RichTextEditorState>[];
	private readonly _reducer: IValueReducer<string| nil>;
	private readonly _intermediateStateHandlers: RichTextEditorIntermediateStateHandlers;

	public readonly value: RichTextEditorState;
	public readonly isInvalid: boolean;
	public readonly validation: IInputValidation;
	public readonly trackingId: string;

	constructor(
		reducer: IValueReducer<string | nil>,
		validation: IValueValidation<string | nil>,
		intermediateStateHandlers: RichTextEditorIntermediateStateHandlers,
		subTrackingId: string
	) {
		this._extraOnBlurHandlers = [];
		this._extraOnChangeHandlers = [];
		this._reducer = reducer;
		this._intermediateStateHandlers = intermediateStateHandlers;

		this.value = intermediateStateHandlers.intermediateState;
		this.validation = validation;
		this.isInvalid = this.validation
			? this.validation.getValidationState().kind === InputValidationResultKind.Invalid
			: false;

		this.trackingId = reducer.path + subTrackingId;

		this.onChange = this.onChange.bind(this);
		this.onBlur = this.onBlur.bind(this);
		this.withOnChange = this.withOnChange.bind(this);
		this.withOnBlur = this.withOnBlur.bind(this);

		this.updateStateFromValueIfNeeded();
	}

	public onChange(newValue: RichTextEditorState, isSignificant: boolean): void {
		if (!isProseMirror(newValue)) {
			throw new Error("This function only works with prosemirror state");
		}

		const {
			setIntermediateState,
			setHtmlThatRepresentsTheState
		} = this._intermediateStateHandlers;

		setIntermediateState(newValue);

		if (isSignificant) {
			const html: string = getHtml(newValue.editorState);
			this._reducer.setValue(html);
			setHtmlThatRepresentsTheState(html);
		}

		this.validation.triggerValueChanged();

		for (const handler of this._extraOnChangeHandlers) {
			handler(newValue);
		}
	}

	public onBlur(newValue: RichTextEditorState): void {
		if (!isProseMirror(newValue)) {
			throw new Error("This function only works with prosemirror state");
		}

		const {
			setIntermediateState,
			setHtmlThatRepresentsTheState
		} = this._intermediateStateHandlers;

		const html: string = getHtml(newValue.editorState);
		this._reducer.setValue(html);

		setIntermediateState(newValue);
		setHtmlThatRepresentsTheState(html);

		this.validation.triggerInputFinished();

		for (const handler of this._extraOnBlurHandlers) {
			handler(newValue);
		}
	}

	public withOnChange(handler: (newValue: RichTextEditorState) => void): IAttachableRichTextEditorPropsWithBuilder {
		this._extraOnChangeHandlers.push(handler);
		return this;
	}

	public withOnBlur(handler: (newValue: RichTextEditorState) => void): IAttachableRichTextEditorPropsWithBuilder {
		this._extraOnBlurHandlers.push(handler);
		return this;
	}

	private updateStateFromValueIfNeeded(): void {
		const {
			getHtmlThatRepresentsTheState,
			setIntermediateState,
			setHtmlThatRepresentsTheState
		} = this._intermediateStateHandlers;

		const currentHtml: string = this._reducer.value ?? "";
		if (currentHtml === getHtmlThatRepresentsTheState()) {
			return;
		}

		setIntermediateState(previousEditorState => {
			if (!isProseMirror(previousEditorState)) {
				throw new Error("This method only support prosemirror state");
			}

			return {
				kind: RichTextEditorStateKind.ProseMirror,
				editorState: setHtml(
					previousEditorState.editorState,
					currentHtml,
					false
				)
			};
		});
		setHtmlThatRepresentsTheState(currentHtml);
	}
}

export type IResolveAttachableRichTextEditorPropsWithBuilder<TValue> = TValue extends string
	? IAttachableRichTextEditorPropsWithBuilder
	: never;